import axios, { type AxiosRequestConfig } from 'axios'
import { getCookie, removeCookie } from '@/utils/cookie'
import { getAnonymousIdentity } from '@/utils/identity'
import { DISPLAY_NAME_KEY, TOKEN_KEY } from '@/stores/auth'

export interface ApiResponse<T = any> {
  code: number
  message?: string
  data: T
}

export class ApiError extends Error {
  code?: number
  constructor(message: string, code?: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

const client = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API,
  timeout: 50_000,
})

client.interceptors.request.use((config) => {
  const token = getCookie(TOKEN_KEY)
  const locale = getCookie('locale') || 'en'
  config.headers.set('platform', '40')
  config.headers.set('lang', locale === 'zh-CN' ? 'zh' : locale)
  config.headers.set('identity', getAnonymousIdentity())
  if (token) {
    config.headers.set('Y-Token', token)
    config.headers.set('authorization', `Bearer ${token}`)
  }
  config.params = { ...(config.params || {}), channel: 40 }
  return config
})

client.interceptors.response.use(
  (response) => {
    const payload = response.data as ApiResponse
    if (payload?.code !== 200) {
      const authCodes = [50008, 50012, 50014, 50009]
      if (authCodes.includes(payload?.code)) {
        removeCookie(TOKEN_KEY)
        removeCookie(DISPLAY_NAME_KEY)
        window.dispatchEvent(new CustomEvent('findbao:auth-expired'))
      }
      throw new ApiError(payload?.message || 'Request failed', payload?.code)
    }
    return payload as any
  },
  (error) => {
    const message = error?.response?.data?.message || error?.message || 'Network error'
    throw new ApiError(message, error?.response?.data?.code)
  },
)

export async function request<T = any>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
  return client.request(config) as unknown as Promise<ApiResponse<T>>
}

export default client
